import { get, setWith, toPath } from 'lodash';
import { ReactNode } from 'react';

import { ErrorOrWarning } from '../ErrorHelpers/ErrorHelpers';
import { ErrorMapper } from '../ErrorsMapper/ErrorsMapper';

const DEFAULT_PATH_INDEX = '0';

interface SingleFieldErrorMapper {
    serverPath: Array<string>;
    clientPath: Array<string>;
}

export class EditPageBackToFrontErrorMapper {
    constructor(
        private domain: string,
        private fieldsData: object,
        private ids: Array<string>,
        private errorMapper: ErrorMapper,
        private indexStub: string
    ) {}

    public mapServerErrorsToClientError(errors: Array<ErrorOrWarning>): object {
        return errors.reduce((acc, error) => {
            const errorPath = this.mapServerErrorPathToClientPath(error).join(
                '.'
            );

            const errorObj = {
                text: this.errorMapper({
                    code: error.code,
                    params: error.params,
                    path: errorPath
                }),
                code: error.code
            };

            const fieldErrors = get(acc, errorPath);

            if (!fieldErrors) {
                setWith(acc, errorPath, [errorObj], Object);
            } else {
                // todo: убрать проверку на уникальность кода после исправления формата ошибки
                // на максимальное количество условий ретаргетина на сервере DIRECT-94417
                if (
                    !fieldErrors.find(
                        (fieldError: { code: string; text: ReactNode }) =>
                            fieldError.code === errorObj.code
                    )
                ) {
                    fieldErrors.push(errorObj);
                }
            }

            return acc;
        }, {});
    }

    private mapServerErrorPathToClientPath(
        error: ErrorOrWarning
    ): Array<string> {
        const [
            serverErrorDomain,
            serverErrorItemIndex,
            ...gotFromServerPath
        ] = toPath(error.path);
        const clientPaths = this.fieldsData[serverErrorDomain];

        if (!clientPaths) {
            // если ошибка не имеет префикса, специфичного для ручки
            return ['common', 'errors'];
        }

        const clientItemId = this.ids[serverErrorItemIndex];

        if (!clientItemId) {
            // если ошибка не указывает на конкретную группу
            return ['common', 'errors'];
        }

        const foundItem = clientPaths.find(
            ({ serverPath }: SingleFieldErrorMapper) => {
                // проверяя длину, исключаем частичные совпадения
                if (gotFromServerPath.length !== serverPath.length) {
                    return false;
                }

                return gotFromServerPath.every(
                    (pathItem: string, idx: number): boolean => {
                        if (/\d+/.test(pathItem)) {
                            // числовые позиции должны обозначаться INDEX'ом в декларации
                            return serverPath[idx] === this.indexStub;
                        }
                        return pathItem === serverPath[idx];
                    }
                );
            }
        );

        if (!foundItem) {
            // если полученный с бэкенда путь незадекларирован
            return [this.domain, clientItemId, 'common', 'errors'];
        }

        const gotFromServerIndexes = gotFromServerPath.reduce(
            (acc: Array<string>, item: string): Array<string> => {
                if (/\d+/.test(item)) {
                    return acc.concat(item);
                }
                return acc;
            },
            []
        );

        return [this.domain, clientItemId].concat(
            foundItem.clientPath.map(
                (pathItem: string): string => {
                    if (pathItem === this.indexStub) {
                        const indexFromServer = gotFromServerIndexes.shift() as string;
                        // подставляем в клиентский путь индекс сущности, полученный с сервера
                        return indexFromServer
                            ? indexFromServer
                            : DEFAULT_PATH_INDEX;
                    }
                    return pathItem;
                }
            ),
            'errors'
        );
    }
}
